/**
 * Feature Limits Configuration
 *
 * Defines the access and usage limits for each billing feature per plan.
 *
 * KEY PRINCIPLE: Tiers differ on RESOURCES, not features.
 * Every paid user gets the full product. Upgrade incentive comes from
 * needing more prompts, engines, projects, or faster scans.
 */

import {
  BillingFeature,
  BillingProduct,
  PURCHASABLE_PRODUCTS_ORDERED,
} from "./constants";
import {
  MAX_ENGINES_BY_PLAN,
  MAX_PROMPTS_BY_PLAN,
  MONTHLY_SCAN_RUN_ALLOWANCE as SHARED_MONTHLY_SCAN_RUN_ALLOWANCE,
  remainingScanRuns as sharedRemainingScanRuns,
} from "@ripenn/temporal/scan-config";
import {
  startOfDay,
  startOfWeek,
  startOfMonth,
  addDays,
  addWeeks,
  addMonths,
  differenceInCalendarMonths,
  differenceInDays,
  differenceInWeeks,
  isAfter,
} from "date-fns";
import { UTCDateMini } from "@date-fns/utc";

// ============================================================================
// Types
// ============================================================================

/**
 * Period over which usage is measured
 */
export enum FeatureLimitPeriod {
  DAILY = "daily",
  WEEKLY = "weekly",
  MONTHLY = "monthly",
}

/**
 * Type of limit applied to a feature
 */
export enum FeatureLimitType {
  UNLIMITED = "unlimited",
  BOUNDED = "bounded",
  NO_ACCESS = "no_access",
}

/**
 * Base interface for all feature limits
 */
interface BaseFeatureLimit {
  feature: BillingFeature;
  type: FeatureLimitType;
}

/**
 * Unlimited access to a feature
 */
export interface UnlimitedFeatureLimit extends BaseFeatureLimit {
  type: FeatureLimitType.UNLIMITED;
}

/**
 * Bounded access with usage limits
 */
export interface BoundedFeatureLimit extends BaseFeatureLimit {
  type: FeatureLimitType.BOUNDED;
  period: FeatureLimitPeriod;
  limit: number;
}

/**
 * No access to a feature
 */
export interface NoAccessFeatureLimit extends BaseFeatureLimit {
  type: FeatureLimitType.NO_ACCESS;
}

export type FeatureLimit =
  | UnlimitedFeatureLimit
  | BoundedFeatureLimit
  | NoAccessFeatureLimit;

// ============================================================================
// Helper Functions for Creating Limits
// ============================================================================

function unlimited(feature: BillingFeature): UnlimitedFeatureLimit {
  return {
    feature,
    type: FeatureLimitType.UNLIMITED,
  };
}

function bounded(
  feature: BillingFeature,
  period: FeatureLimitPeriod,
  limit: number,
): BoundedFeatureLimit {
  if (!Number.isInteger(limit) || limit < 1) {
    throw new TypeError(
      `Feature limit must be a positive integer, got: ${limit}`,
    );
  }
  return {
    feature,
    type: FeatureLimitType.BOUNDED,
    period,
    limit,
  };
}

function noAccess(feature: BillingFeature): NoAccessFeatureLimit {
  return {
    feature,
    type: FeatureLimitType.NO_ACCESS,
  };
}

// ============================================================================
// Deprecated features (removed from product)
// ============================================================================

/**
 * Features that have been removed from the product.
 * Set to noAccess on all plans for safety.
 */
const DEPRECATED_FEATURES: NoAccessFeatureLimit[] = [
  noAccess(BillingFeature.EVALUATIONS),
  noAccess(BillingFeature.PUBLISHING_INTEGRATIONS),
  noAccess(BillingFeature.COLLABORATIVE_EDITING),
];

// ============================================================================
// Plan Configurations
// ============================================================================

/**
 * Feature limits for each billing product/plan.
 *
 * All paid tiers get the full feature set. Tiers differ only on resource
 * limits (see PLAN_RESOURCE_LIMITS) and usage-bounded features below.
 *
 * FREE (default plan, not advertised on pricing page):
 *   Limited self-serve access so new users can experience the product
 *   without entering a credit card. ANALYTICS is the budget that meters
 *   brand-visibility scans (recorded in /api/ai-responses/check), capped
 *   at 1/month. Read-only analytics views (trends, content-stats) check
 *   permission via hasFeatureAccess instead of withOrgFeatureAccess so they
 *   don't burn the scan budget. Combined with PLAN_RESOURCE_LIMITS
 *   (1 project, 1 engine per scan, 10 prompts) total LLM cost per free
 *   user is ~1 small scan/month.
 * SOLO (Starter): 25 content gens/mo, 10 query discoveries/mo
 * PRO (Growth):   100 content gens/mo, 50 query discoveries/mo
 * TEAM (Enterprise): Unlimited everything
 */
export const BILLING_PRODUCT_LIMITS: Record<BillingProduct, FeatureLimit[]> = {
  [BillingProduct.FREE]: [
    noAccess(BillingFeature.CONTENT_GENERATIONS),
    noAccess(BillingFeature.QUERY_DISCOVERY),
    noAccess(BillingFeature.GAP_CONTENT),
    noAccess(BillingFeature.PROMPT_OPTIMIZATION),
    noAccess(BillingFeature.CONTENT_STRATEGY),
    bounded(BillingFeature.ANALYTICS, FeatureLimitPeriod.MONTHLY, 1),
    unlimited(BillingFeature.CONTENT_IMPORT),
    ...DEPRECATED_FEATURES,
  ],
  [BillingProduct.SOLO]: [
    bounded(BillingFeature.CONTENT_GENERATIONS, FeatureLimitPeriod.MONTHLY, 25),
    bounded(BillingFeature.QUERY_DISCOVERY, FeatureLimitPeriod.MONTHLY, 10),
    unlimited(BillingFeature.ANALYTICS),
    unlimited(BillingFeature.CONTENT_IMPORT),
    unlimited(BillingFeature.GAP_CONTENT),
    unlimited(BillingFeature.PROMPT_OPTIMIZATION),
    unlimited(BillingFeature.CONTENT_STRATEGY),
    ...DEPRECATED_FEATURES,
  ],
  [BillingProduct.PRO]: [
    bounded(
      BillingFeature.CONTENT_GENERATIONS,
      FeatureLimitPeriod.MONTHLY,
      100,
    ),
    bounded(BillingFeature.QUERY_DISCOVERY, FeatureLimitPeriod.MONTHLY, 50),
    unlimited(BillingFeature.ANALYTICS),
    unlimited(BillingFeature.CONTENT_IMPORT),
    unlimited(BillingFeature.GAP_CONTENT),
    unlimited(BillingFeature.PROMPT_OPTIMIZATION),
    unlimited(BillingFeature.CONTENT_STRATEGY),
    ...DEPRECATED_FEATURES,
  ],
  [BillingProduct.TEAM]: [
    unlimited(BillingFeature.CONTENT_GENERATIONS),
    unlimited(BillingFeature.ANALYTICS),
    unlimited(BillingFeature.CONTENT_IMPORT),
    unlimited(BillingFeature.GAP_CONTENT),
    unlimited(BillingFeature.QUERY_DISCOVERY),
    unlimited(BillingFeature.PROMPT_OPTIMIZATION),
    unlimited(BillingFeature.CONTENT_STRATEGY),
    ...DEPRECATED_FEATURES,
  ],
};

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get the feature limit configuration for a specific feature and plan
 */
export function getFeatureLimit(
  plan: BillingProduct,
  feature: BillingFeature,
): FeatureLimit | undefined {
  const limits = BILLING_PRODUCT_LIMITS[plan];
  return limits.find((limit) => limit.feature === feature);
}

/**
 * Check if a feature is accessible for a given plan (not NoAccess)
 */
export function hasFeatureAccess(
  plan: BillingProduct,
  feature: BillingFeature,
): boolean {
  const limit = getFeatureLimit(plan, feature);
  if (!limit) {
    return false;
  }
  return limit.type !== FeatureLimitType.NO_ACCESS;
}

/**
 * Get the minimum required plan and available upgrade options for a feature.
 *
 * @param feature - The feature to check
 * @returns Object with requiredPlan (minimum plan that has access) and
 *          upgradeOptions (all plans that provide access)
 */
export function getFeatureUpgradeInfo(feature: BillingFeature): {
  requiredPlan: BillingProduct | null;
  upgradeOptions: BillingProduct[];
} {
  const plansWithAccess = PURCHASABLE_PRODUCTS_ORDERED.filter((plan) =>
    hasFeatureAccess(plan, feature),
  );

  return {
    requiredPlan: plansWithAccess[0] ?? null,
    upgradeOptions: plansWithAccess,
  };
}

/**
 * Get the usage limit for a bounded feature
 * Returns null if unlimited or no access
 */
export function getUsageLimit(
  plan: BillingProduct,
  feature: BillingFeature,
): { limit: number; period: FeatureLimitPeriod } | null {
  const featureLimit = getFeatureLimit(plan, feature);
  if (featureLimit?.type === FeatureLimitType.BOUNDED) {
    return {
      limit: featureLimit.limit,
      period: featureLimit.period,
    };
  }
  return null;
}

/**
 * Get the start date for the current billing period.
 *
 * If a subscription start date is provided, calculates the period based on
 * the subscription anniversary (e.g., if subscribed on Jan 15, monthly periods
 * are 15th-14th). Otherwise falls back to calendar-based periods.
 *
 * @param period - The period type (daily, weekly, monthly)
 * @param subscriptionStart - When the user's subscription started (null for calendar-based)
 * @param asOf - The reference date (defaults to now, useful for testing)
 * @returns The start date of the current billing period
 */
export function getPeriodStartDate(
  period: FeatureLimitPeriod,
  subscriptionStart: Date | null = null,
  asOf: Date = new Date(),
): Date {
  if (!subscriptionStart) {
    return getCalendarPeriodStartDate(period, asOf);
  }

  if (isAfter(subscriptionStart, asOf)) {
    throw new TypeError(
      `Subscription start date (${subscriptionStart.toISOString()}) ` +
        `cannot be after reference date (${asOf.toISOString()})`,
    );
  }

  return getSubscriptionPeriodStartDate(period, subscriptionStart, asOf);
}

/**
 * Calculate period start based on calendar boundaries (UTC).
 * Used when subscription start is unknown.
 */
function getCalendarPeriodStartDate(
  period: FeatureLimitPeriod,
  asOf: Date,
): Date {
  const utcDate = new UTCDateMini(asOf.getTime());

  switch (period) {
    case FeatureLimitPeriod.DAILY:
      return startOfDay(utcDate);

    case FeatureLimitPeriod.WEEKLY:
      return startOfWeek(utcDate, { weekStartsOn: 1 });

    case FeatureLimitPeriod.MONTHLY:
      return startOfMonth(utcDate);
  }
}

/**
 * Calculate period start based on subscription anniversary (UTC).
 * Periods are time-aligned: they start at the exact time the subscription began.
 */
function getSubscriptionPeriodStartDate(
  period: FeatureLimitPeriod,
  subscriptionStart: Date,
  asOf: Date,
): Date {
  const utcSubscriptionStart = new UTCDateMini(subscriptionStart.getTime());
  const utcAsOf = new UTCDateMini(asOf.getTime());

  switch (period) {
    case FeatureLimitPeriod.DAILY: {
      const daysDiff = differenceInDays(utcAsOf, utcSubscriptionStart);
      const periodStart = addDays(utcSubscriptionStart, daysDiff);
      return periodStart;
    }

    case FeatureLimitPeriod.WEEKLY: {
      const weeksDiff = differenceInWeeks(utcAsOf, utcSubscriptionStart);
      const periodStart = addWeeks(utcSubscriptionStart, weeksDiff);
      return periodStart;
    }

    case FeatureLimitPeriod.MONTHLY: {
      // Find the largest N where addMonths(sub, N) <= asOf. The
      // calendar-month diff is the upper bound — addMonths(sub, calDiff+1)
      // always lands in a strictly later month — and it's the right
      // answer when the resulting anniversary doesn't overshoot asOf.
      // We can't use differenceInMonths here: it under-counts by 1 when
      // day-clamping puts the next anniversary on the same calendar
      // date as asOf at an earlier time of day (e.g. day-31 anchor
      // produces a Jun 30 00:00 boundary while asOf is Jun 30 23:00).
      const calMonthDiff = differenceInCalendarMonths(
        utcAsOf,
        utcSubscriptionStart,
      );
      const candidate = addMonths(utcSubscriptionStart, calMonthDiff);
      if (candidate.getTime() <= utcAsOf.getTime()) {
        return candidate;
      }
      return addMonths(utcSubscriptionStart, calMonthDiff - 1);
    }
  }
}

/**
 * Human-readable feature names for display
 */
export const FEATURE_DISPLAY_NAMES: Record<BillingFeature, string> = {
  [BillingFeature.CONTENT_GENERATIONS]: "AI Content Generations",
  [BillingFeature.CONTENT_STRATEGY]: "Content Strategy",
  [BillingFeature.ANALYTICS]: "Analytics",
  [BillingFeature.EVALUATIONS]: "Content Evaluations",
  [BillingFeature.PUBLISHING_INTEGRATIONS]: "Publishing Integrations",
  [BillingFeature.COLLABORATIVE_EDITING]: "Collaborative Editing",
  [BillingFeature.CONTENT_IMPORT]: "Content Import",
  [BillingFeature.GAP_CONTENT]: "Gap Analysis",
  [BillingFeature.QUERY_DISCOVERY]: "Query Discovery",
  [BillingFeature.PROMPT_OPTIMIZATION]: "Prompt Optimization",
};

/**
 * Get the display name for a feature
 */
export function getFeatureDisplayName(feature: BillingFeature): string {
  return FEATURE_DISPLAY_NAMES[feature] || feature;
}

/**
 * Human-readable period names
 */
export const PERIOD_DISPLAY_NAMES: Record<FeatureLimitPeriod, string> = {
  [FeatureLimitPeriod.DAILY]: "day",
  [FeatureLimitPeriod.WEEKLY]: "week",
  [FeatureLimitPeriod.MONTHLY]: "month",
};

// ============================================================================
// Resource Limits (entity count caps, enforced via DB count queries)
// ============================================================================

/**
 * Hard limits on how many entities a user can have per plan.
 * null = unlimited.
 *
 * Unlike feature limits (which gate time-period-based usage), resource limits
 * cap the total number of active entities a user can maintain at once.
 */
export interface PlanResourceLimits {
  /** Max active brand prompts tracked per project */
  maxPromptsPerProject: number | null;
  /** Max competitors tracked per project */
  maxCompetitorsPerProject: number | null;
  /** Max AI engines that can be selected for a single scan */
  maxEnginesPerScan: number | null;
  /** Max projects a user can create */
  maxProjects: number | null;
}

/**
 * Resource limits per plan.
 *
 * Per-project limits — each project gets its full prompt allocation.
 *
 * | Plan              | Prompts | Competitors | Engines | Projects |
 * |-------------------|---------|-------------|---------|----------|
 * | FREE (default)    | 10      | 3           | 1       | 1        |
 * | Starter (solo)    | 100     | 5           | 2       | 1        |
 * | Growth (pro)      | 500     | 20          | 4       | 3        |
 * | Enterprise (team) | ∞       | ∞           | ∞       | ∞        |
 */
export const PLAN_RESOURCE_LIMITS: Record<BillingProduct, PlanResourceLimits> =
  {
    [BillingProduct.FREE]: {
      maxPromptsPerProject: MAX_PROMPTS_BY_PLAN.free,
      maxCompetitorsPerProject: 3,
      maxEnginesPerScan: MAX_ENGINES_BY_PLAN.free,
      maxProjects: 1,
    },
    [BillingProduct.SOLO]: {
      maxPromptsPerProject: MAX_PROMPTS_BY_PLAN.solo,
      maxCompetitorsPerProject: 5,
      maxEnginesPerScan: MAX_ENGINES_BY_PLAN.solo,
      maxProjects: 1,
    },
    [BillingProduct.PRO]: {
      maxPromptsPerProject: MAX_PROMPTS_BY_PLAN.pro,
      maxCompetitorsPerProject: 20,
      maxEnginesPerScan: MAX_ENGINES_BY_PLAN.pro,
      maxProjects: 3,
    },
    [BillingProduct.TEAM]: {
      maxPromptsPerProject: MAX_PROMPTS_BY_PLAN.team,
      maxCompetitorsPerProject: null,
      maxEnginesPerScan: MAX_ENGINES_BY_PLAN.team,
      maxProjects: null,
    },
  };

/**
 * Check whether adding one more item would exceed the plan's resource limit.
 *
 * Pure function — caller provides current count and plan.
 *
 * @param plan - The user's current billing plan
 * @param resource - Which resource limit to check (key of PlanResourceLimits)
 * @param currentCount - How many items currently exist (before the new one)
 * @returns { allowed, limit, current } — allowed is true when adding one more is permitted
 */
export function checkResourceCount(
  plan: BillingProduct,
  resource: keyof PlanResourceLimits,
  currentCount: number,
): { allowed: boolean; limit: number | null; current: number } {
  const limit = PLAN_RESOURCE_LIMITS[plan][resource];
  if (limit === null) {
    return { allowed: true, limit: null, current: currentCount };
  }
  return {
    allowed: currentCount < limit,
    limit,
    current: currentCount,
  };
}

// ============================================================================
// Brand Scan Run Allowance (per-plan monthly runs)
// ============================================================================

/**
 * Per-plan monthly run allowance (manual + scheduled scans combined; one
 * run is one prompt x one engine x one repeat). Counted via existing
 * usage_records for BillingFeature.ANALYTICS.
 *
 * Canonical values live in `@ripenn/temporal/scan-config` (the single TS
 * source of truth, also consumed by the Temporal worker); this record is
 * the BillingProduct-keyed view of them. The SQL mirror in
 * `get_monthly_scan_usage` is pinned to these numbers by sqlproof.
 */
export const MONTHLY_SCAN_RUN_ALLOWANCE: Record<BillingProduct, number | null> =
  {
    [BillingProduct.FREE]: SHARED_MONTHLY_SCAN_RUN_ALLOWANCE.free,
    [BillingProduct.SOLO]: SHARED_MONTHLY_SCAN_RUN_ALLOWANCE.solo,
    [BillingProduct.PRO]: SHARED_MONTHLY_SCAN_RUN_ALLOWANCE.pro,
    [BillingProduct.TEAM]: SHARED_MONTHLY_SCAN_RUN_ALLOWANCE.team,
  };

/**
 * Pure allowance check. Returns { used, limit, remaining }; remaining is
 * max(0, limit - used). Delegates to the canonical implementation in
 * `@ripenn/temporal/scan-config`.
 */
export function remainingScanRuns(
  plan: BillingProduct,
  used: number,
): { used: number; limit: number; remaining: number } {
  return sharedRemainingScanRuns(plan, used);
}
